import React from 'react'
import throttle from 'lodash.throttle'
import { useAuth0 } from '@auth0/auth0-react'
import { useHistory, useLocation } from 'react-router'
import { Routes } from '../../Routes'

const AuthContext = React.createContext(null)

const RELOAD_DELAY = 2500

const AuthProvider = ({ children }) => {
  const location = useLocation()
  const history = useHistory()
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0()
  const isAuth0Auth = isAuthenticated && !isLoading
  const pathName = React.useMemo(() => {
    let path = location.pathname

    if (path.endsWith('/') && path !== '/') {
      path = path.substr(0, path.length - 1)
    }

    return path
  }, [location])

  const onProtectedPath = React.useMemo(() => {
    if (pathName === '/') {
      return true
    } else {
      return Object.values(Routes).some(
        route =>
          ![Routes.LOGIN, Routes.LOGOUT, '/'].includes(route) &&
          pathName.startsWith(route)
      )
    }
  }, [pathName])

  React.useEffect(() => {
    const getToken = async () => {
      try {
        const token = await getAccessTokenSilently({ detailedResponse: true })
        localStorage.setItem('session_token', token.access_token)
      } catch (error) {
        console.error('error getting token', error)
        history.push(Routes.LOGOUT)
      }
    }
    if (isAuth0Auth) {
      getToken()
    }
  }, [isAuth0Auth, getAccessTokenSilently, history])

  // Keeps stored token up to date for id
  React.useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    const timer = setInterval(async () => {
      try {
        const token = await getAccessTokenSilently()
        localStorage.setItem('session_token', token)
      } catch (err) {
        console.error('error getting token', err)
      }
    }, 1000 * 60 * 60)

    return () => {
      clearInterval(timer)
    }
  }, [isAuthenticated, getAccessTokenSilently])

  // Set flags to refresh token
  const startReload = React.useCallback(() => {
    if (window.localStorage.getItem('refreshed') !== 'true') {
      localStorage.setItem('refreshed', 'true')
      localStorage.removeItem('nextRefreshDate')
      window.location.reload()
    }
  }, [])

  // Reloads the page only once in a period
  // eslint-disable-next-line
  const throttleReload = React.useCallback(
    throttle(startReload, RELOAD_DELAY),
    [startReload]
  )

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn:
          onProtectedPath &&
          isAuth0Auth &&
          !!localStorage.getItem('session_token'),
        isLoading,
        pathName,
        token: localStorage.getItem('session_token') ?? null,
        handleReload: throttleReload
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider

export function useAuth () {
  return React.useContext(AuthContext)
}
